import { t } from 'i18next';
import React, { useEffect } from 'react';
import ProfileAvatar from '../../../components/thumbnails/ProfileAvatar';
import type { BaseFootprint } from '../../../domain/models/locations/BaseFootprint';
import { ReactComponent as CloseIcon } from '../../../icons/xmark.svg';
import LinkPreviewBox from '../link-preview/LinkPreviewBox';

interface FootprintDetailModalProps {
  footprint: BaseFootprint | null;
  isOpen: boolean;
  onClose: () => void;
}

const FootprintDetailModal: React.FC<FootprintDetailModalProps> = ({
  footprint,
  isOpen,
  onClose
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !footprint) return null;

  return (
    <div className="fixed inset-0 z-[999999]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-[20px] shadow-xl z-50 w-[calc(100%-48px)]">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-0 top-[-36px] z-10 rounded-full p-[7px] bg-black/35"
        >
          <CloseIcon className="w-[16px] h-[16px] fill-white" />
        </button>

        {/* Content Container */}
        <div className="flex flex-col">
          {/* Header Image */}

          {/* Profile Info Overlay */}
          <div className="m-[16px]">
            <div className="flex items-center">
              <ProfileAvatar
                profileImageUrl={
                  footprint.reporter?.thumbnailUrl ?? '/assets/icons/thumbnail.ghost.profile.png'
                }
                widthAndHeight="34px"
                onClick={() => {}}
              />
              <div className="flex flex-col px-[12px]">
                <span className="text-[14px] font-semibold text-main-light-2 dark:text-main-dark-2">
                  {footprint.reporter?.nickname ?? t('anonymous')}
                </span>
                {footprint.reporter?.description && (
                  <span className="text-[12px] font-normal text-main-light-3 dark:text-main-dark-3">
                    {footprint.reporter.description}
                  </span>
                )}
              </div>
            </div>
          </div>

          {footprint.baseThumbnailUrl && (
            <div className="mx-[10px] mb-[16px]">
              <img
                src={footprint.baseThumbnailUrl}
                alt={footprint.footprinterName}
                className="w-full h-full object-cover rounded-[7px]"
              />
            </div>
          )}

          {/* Description */}
          {footprint.description && (
            <div className="flex flex-col mt-[10px] mx-[18px] mb-[32px] ">
              <p className="font-normal text-[15px] whitespace-pre-wrap">{footprint.description}</p>
            </div>
          )}

          {/* Link Preview */}
          {footprint.linkUrl && (
            <div className="mb-[9px] px-[9px]">
              <LinkPreviewBox url={footprint.linkUrl} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FootprintDetailModal;
